// src/backendServices/liveStreamManagment/api_regieConfig.ts
// Service API pour la sauvegarde / chargement de configurations de régie.

import { RegieConfigPayload } from '../../components/block/liveStreamManagment/RegieConfigModal';
import { API_URLS } from '../urls';
import { FileUploaderWithStore } from '../fileManagment/uploadWithStore';
import { isStreamedMediaUrl } from '../../dataTypes/liveStreamManagment/utils_videoSource';

// ── Types retour API ──────────────────────────────────────────────────────────

export interface RegieConfigRecord {
  id        : number;
  name      : string;
  createdAt : string;   // ISO string
  config    : RegieConfigPayload;
}

// ── Endpoints ─────────────────────────────────────────────────────────────────

/**
 * Sauvegarde une configuration de régie pour un canal donné.
 * POST /api/regie-configs/{channelUuid}
 */
export async function saveRegieConfig(
  channelUuid : string,
  payload     : RegieConfigPayload,
): Promise<RegieConfigRecord> {

  console.log('saveRegieConfig', channelUuid, payload);

  const url = `${API_URLS.saveRegieConfig}`;

  console.log('saveRegieConfig', url);

  const response = await fetch(url, {
    method : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body   : JSON.stringify({ ...payload, channelUuid }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Erreur ${response.status} : ${text || response.statusText}`);
  }

  return response.json() as Promise<RegieConfigRecord>;
}

/**
 * Récupère toutes les configurations sauvegardées pour un canal.
 * GET /api/regie-configs?channelUuid={channelUuid}
 */
export async function getRegieConfigs(
  channelUuid : string,
): Promise<RegieConfigRecord[]> {

  console.log('getRegieConfigs', channelUuid);

  const url = `${API_URLS.getRegieConfigs}?channelUuid=${encodeURIComponent(channelUuid)}`;

  console.log('getRegieConfigs', url);

  const response = await fetch(url);

  console.log('getRegieConfigs', response);

  if (!response.ok) {
    throw new Error(`Erreur ${response.status}`);
  }

  return response.json() as Promise<RegieConfigRecord[]>;
}

/**
 * Supprime une configuration de régie par son id.
 * DELETE /api/regie-configs/{id}
 */
export async function deleteRegieConfig(id: number): Promise<void> {

  console.log('deleteRegieConfig', id);

  const baseUrl = `${API_URLS.deleteRegieConfig}`;
  const url     = baseUrl.replace('{id}', `${id}`);

  console.log('deleteRegieConfig', url);

  const response = await fetch(url, { method: 'DELETE' });

  console.log('deleteRegieConfig', response);

  if (!response.ok) {
    throw new Error(`Erreur ${response.status}`);
  }
}

/**
 * Déclenche la génération du ZIP côté serveur et le télécharge.
 * GET /api/regie_configs/{id}/export_zip
 */
export async function downloadRegieConfigZip(regieConfigId: number, filename: string): Promise<void> {
  const url = `${API_URLS.exportRegieConfigZip}`.replace('{id}', `${regieConfigId}`);

  const response = await fetch(url, { method: 'GET' });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Erreur génération ZIP ${response.status} : ${text || response.statusText}`);
  }

  const blob      = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a         = document.createElement('a');
  a.href          = objectUrl;
  a.download      = filename.replace(/[^a-z0-9_\-. ]/gi, '_') + '.zip';
  a.click();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Télécharge un fichier média individuel depuis le serveur.
 * GET /api/regie_configs/{id}/media/{filename}
 *
 * filename = {mediaType}_{slotId}_{role}.{ext}
 * ex : image_img-0_slot.png, scene_scene-0_background.jpg
 *
 * Retourne un Blob prêt à être stocké dans IndexedDB via storeMediaBlob().
 */
export async function downloadRegieConfigMedia(
  regieConfigId : number,
  filename      : string,
): Promise<Blob> {
  const url = `${API_URLS.downloadRegieMedia}`
    .replace('{id}', `${regieConfigId}`)
    + `?filename=${encodeURIComponent(filename)}`;

  const response = await fetch(url, { method: 'GET' });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Erreur download média ${response.status} : ${text || response.statusText}`);
  }

  return response.blob();
}

// ── Upload des fichiers média ──────────────────────────────────────────────────

export type RegieMediaType = 'image' | 'sound' | 'video' | 'scene';

// Entrée normalisée utilisée en interne pour chaque fichier à uploader
interface MediaEntry {
  slotId   : string;
  mediaType: RegieMediaType;
  url      : string;
  role     : string; // 'slot' | 'background' | 'incrustation-{id}'
  originalName?: string; // sert d'extension de repli quand le blob n'a pas de type
  fileType?: string; // type réel du fichier ('image' | 'video' | 'sound'), utile pour les scènes
}

/**
 * Construit la liste plate de tous les fichiers à uploader depuis des slots simples
 * (ImageSlot, SoundSlot, JingleSlot — qui ont tous une url à la racine).
 */
function flattenSimpleSlots(
  mediaType : RegieMediaType,
  slots     : Array<{ id: string; url?: string; [key: string]: any }>,
): MediaEntry[] {
  return slots
    .filter(s => !!s.url && !isStreamedMediaUrl(s.url))   // vidéo HLS du compte : pas de fichier
    .map(s => ({ slotId: s.id, mediaType, url: s.url!, role: 'slot', originalName: s.originalName }));
}

/**
 * Construit la liste plate de tous les fichiers à uploader depuis des LayoutSlots.
 */
function flattenSceneSlots(
  slots: Array<{
    id           : string;
    background   : { type: string; url: string };
    incrustations: Array<{ id: string; type: string; url: string }>;
    [key: string]: any;
  }>,
): MediaEntry[] {
  const entries: MediaEntry[] = [];

  slots.forEach(slot => {
    if ((slot.background.type === 'image' || slot.background.type === 'video' || slot.background.type === 'sound') && slot.background.url && !isStreamedMediaUrl(slot.background.url)) {
      entries.push({
        slotId   : slot.id,
        mediaType: 'scene',
        url      : slot.background.url,
        role     : 'background',
        fileType : slot.background.type,
      });
    }
    slot.incrustations.forEach(inc => {
      if ((inc.type === 'image' || inc.type === 'video' || inc.type === 'sound') && inc.url && !isStreamedMediaUrl(inc.url)) {
        entries.push({
          slotId   : slot.id,
          mediaType: 'scene',
          url      : inc.url,
          role     : `incrustation-${inc.id}`,
          fileType : inc.type,
        });
      }
    });
  });

  return entries;
}

/**
 * Uploade un fichier média via FileUploaderWithStore.
 */
async function uploadMediaEntry(regieConfigId: number, entry: MediaEntry): Promise<void> {
  try {
    const fetchResponse = await fetch(entry.url);
    if (!fetchResponse.ok) {
      console.warn(`[RegieMedia] Impossible de récupérer ${entry.role}/${entry.slotId} (${fetchResponse.status})`);
      return;
    }

    const blob     = await fetchResponse.blob();
    const ext      = blob.type ? blob.type.split('/')[1]?.split(';')[0] ?? 'bin' : (entry.originalName?.split('.').pop()?.toLowerCase() || 'bin');
    const filename = `${entry.mediaType}_${entry.slotId}_${entry.role}.${ext}`;
    const file     = new File([blob], filename, { type: blob.type });

    const metadata = {
      regieConfigId,
      mediaType : entry.mediaType,
      slotId    : entry.slotId,
      role      : entry.role,
      filename,
      fileType  : entry.fileType ?? '',
    };

    const uploadConfig = {
      uploadChunkUrl   : API_URLS.uploadRegieMediaChunk,
      finalizeUploadUrl: API_URLS.finalizeRegieMediaUpload,
      chunkSize        : 5 * 1024 * 1024,
      maxRetries       : 3,
      enableResume     : true,
      entityId         : regieConfigId,
      entityType       : 'regie_media',
      autoStart        : false,
      metadata,
    };

    const uploader = new FileUploaderWithStore(file, uploadConfig);
    console.log(`[RegieMedia] Upload démarré — config ${regieConfigId}, ${entry.mediaType}/${entry.role}/${entry.slotId}, ID: ${uploader.id}`);

    await uploader.start(metadata as any);

  } catch (e) {
    console.warn(`[RegieMedia] Erreur upload ${entry.role}/${entry.slotId}:`, e);
  }
}

/**
 * Téléverse les fichiers média des slots simples (image, son, vidéo).
 */
export function uploadRegieConfigMedia(
  regieConfigId : number,
  mediaType     : Exclude<RegieMediaType, 'scene'>,
  slots         : Array<{ id: string; url?: string; [key: string]: any }>,
): Promise<void>[] {
  return flattenSimpleSlots(mediaType, slots).map(entry => uploadMediaEntry(regieConfigId, entry));
}

/**
 * Téléverse tous les fichiers média des scènes (fonds + incrustations).
 */
export function uploadRegieConfigSceneMedia(
  regieConfigId : number,
  layoutSlots   : Array<{
    id           : string;
    background   : { type: string; url: string };
    incrustations: Array<{ id: string; type: string; url: string }>;
    [key: string]: any;
  }>,
): Promise<void>[] {
  return flattenSceneSlots(layoutSlots).map(entry => uploadMediaEntry(regieConfigId, entry));
}